import React from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import PageNotFoundInner from "@components/Error/PageNotFoundInner";
import ServerError from "@components/Error/ServerError";
import NoSelected from "@components/NoSelected";
import Spinner from "@components/Spinner/Spinner";
import { CloseIcon } from "@components/icons";
import { formatPersonaLabel } from "@helper/permissions";
import { getAllProjects } from "@modules/Projects/services/projectCrud";
import { getUser } from "../services/userCrud";
import "../../Sensors/components/SensorDetail.css";

const UserDetailRow = ({ label, children }) => (
  <div className="entity-detail-data-row">
    <div className="entity-detail-data-label">{label}</div>
    <div className="entity-detail-data-value">{children}</div>
  </div>
);

const roleLabel = (t, user) => {
  const role = String(
    user?.role || user?.role_name || user?.account_role || user?.user_role || ""
  ).toLowerCase();

  return (
    {
      superuser: t("superuser"),
      admin: t("admin"),
      basic: t("user"),
    }[role] || "-"
  );
};

const projectNames = (user, projects) => {
  const projectMap = new Map((projects || []).map((project) => [Number(project.id), project.name]));
  const names = Array.from(
    new Set(
      (user?.profile?.areas || [])
        .map((area) => projectMap.get(Number(area?.project_id ?? area?.project?.id)))
        .filter(Boolean)
    )
  );
  return names.join(", ") || "-";
};

const areaNames = (user) =>
  Array.from(new Set((user?.profile?.areas || []).map((area) => area?.name).filter(Boolean))).join(
    ", "
  ) || "-";

const addressLine = (user) =>
  [
    user?.profile?.address?.streetName,
    user?.profile?.address?.houseNumber,
    user?.profile?.address?.zipCode,
    user?.profile?.address?.city,
    user?.profile?.address?.country,
  ]
    .filter(Boolean)
    .join(", ") || "-";

export default function UserDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: user,
    isLoading,
    isError,
    error,
  } = useQuery(["user", id], () => getUser(id), {
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!id,
  });
  const { data: projectsData } = useQuery("user-detail-projects", getAllProjects, {
    refetchOnWindowFocus: false,
    retry: false,
  });

  if (!id) {
    return (
      <Card className="h-100 sensor-detail-panel entity-detail-panel" role="no-selected-card">
        <Card.Body className="sensor-detail-panel-body entity-detail-body">
          <NoSelected text={"user_details.no_user_selected"} />
        </Card.Body>
      </Card>
    );
  }

  if (isLoading) return <Spinner height={"80vh"} />;
  if (isError && error.response?.status === 404) return <PageNotFoundInner />;
  if (isError) return <ServerError />;

  return (
    <Card className="h-100 sensor-detail-panel entity-detail-panel">
      <Card.Header className="sensor-detail-panel-header entity-detail-header">
        <div className="d-flex align-items-center justify-content-between gap-2 w-100 entity-detail-header-row">
          <button
            type="button"
            className="sensor-detail-close-btn entity-detail-control"
            onClick={() => navigate("/users")}
            aria-label={t("close")}
            title={t("close")}
          >
            <CloseIcon />
          </button>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <h5 className="mb-0 entity-detail-title text-truncate">
              {user?.first_name || ""} {user?.last_name || ""}
            </h5>
          </div>
          <div className="sensor-detail-panel-actions d-flex align-items-center gap-2" />
        </div>
      </Card.Header>
      <Card.Body className="sensor-detail-panel-body entity-detail-body">
        <div className="notes mb-4">
          <div className="entity-detail-data">
            <UserDetailRow label={t("email")}>{user?.email || "-"}</UserDetailRow>
            <UserDetailRow label={t("phone_number")}>
              {user?.profile?.phoneNumber || "-"}
            </UserDetailRow>
            <UserDetailRow label={t("role")}>{roleLabel(t, user)}</UserDetailRow>
            <UserDetailRow label={t("persona")}>{formatPersonaLabel(user?.persona)}</UserDetailRow>
          </div>

          <div className="entity-detail-section-header mt-4">
            <h4 className="entity-detail-section-title">{t("projects")}</h4>
          </div>
          <div className="entity-detail-data">
            <UserDetailRow label={t("projects")}>
              {projectNames(user, Array.isArray(projectsData) ? projectsData : [])}
            </UserDetailRow>
          </div>

          <div className="entity-detail-section-header mt-4">
            <h4 className="entity-detail-section-title">{t("areas")}</h4>
          </div>
          <div className="entity-detail-data">
            <UserDetailRow label={t("areas")}>{areaNames(user)}</UserDetailRow>
          </div>

          <div className="entity-detail-section-header mt-4">
            <h4 className="entity-detail-section-title">{t("address")}</h4>
          </div>
          <div className="entity-detail-data">
            <UserDetailRow label={t("address")}>{addressLine(user)}</UserDetailRow>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
